/**
 * My Referral Code screen (POL).
 * Shows referral links with multi-tier reward progress, per-condition claim buttons,
 * and real-time order status for claimed rewards.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  ActivityIndicator,
  Alert,
  Share,
  RefreshControl,
  Modal,
  KeyboardAvoidingView,
  Platform,
  Image,
  Pressable,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import {
  getReferralLinks,
  getReferralAvailable,
  joinPromotion,
  claimReward,
  normalizeReferralPromotions,
  normalizeReferralMediaUri,
} from '../services/referralApi';
import AppHeader from '../components/AppHeader';
import { openZoolyfeIdSso } from '../services/zoolyfeSsoService';
import { goToProfileTab } from '../navigation/goToProfileTab';

const REFERRAL_LAST_STATE_KEY = '@referral_last_state';

function formatClaimedAtTimestamp(ts) {
  if (!ts) return '';
  try {
    return new Date(ts * 1000).toLocaleString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/** Remaining pool for claims; don't coalesce with ?? purchaseCount when 0 is valid. */
function referralRemainingPoints(item) {
  const purchaseCount = item.purchase_count ?? 0;
  const r = item.remaining_transactions;
  if (r === undefined || r === null) return purchaseCount;
  const n = Number(r);
  return Number.isFinite(n) ? Math.max(0, n) : purchaseCount;
}

// ── Tier row component ───────────────────────────────────────────────────────
function TierRow({ cond, index, status, onClaim, claiming, insufficient, remainingForInfo, addressReady, onNeedAddress }) {
  const tierNum = index + 1;
  const termsNeed = Math.max(1, Number(cond.terms_count) || 1);
  const rem = Number(remainingForInfo);
  const remSafe = Number.isFinite(rem) ? rem : 0;

  const statusColor = status === 'claimed'
    ? '#10B981'
    : status === 'claimable'
    ? '#F59E0B'
    : '#94A3B8';

  const statusLabel = status === 'claimable' ? 'Syarat terpenuhi' : null;

  return (
    <View style={[
      tierStyles.row,
      status === 'claimable' && tierStyles.rowClaimable,
      status === 'claimed'   && tierStyles.rowClaimed,
    ]}>
      {/* badge + tier number */}
      <View style={tierStyles.tierHead}>
        <View style={[tierStyles.badge, { backgroundColor: statusColor + '20', borderColor: statusColor }]}>
          {status === 'claimed' ? (
            <Ionicons name="checkmark" size={13} color={statusColor} />
          ) : status === 'claimable' ? (
            <Ionicons name="gift-outline" size={13} color={statusColor} />
          ) : (
            <Ionicons name="lock-closed-outline" size={13} color={statusColor} />
          )}
        </View>
        <Text style={[tierStyles.tierLabel, { color: statusColor }]}>
          Kondisi {tierNum}
        </Text>
        <Text style={tierStyles.termsText}>
          Butuh {termsNeed} poin per klaim
        </Text>
      </View>

      {/* reward image + description */}
      <View style={tierStyles.body}>
        {cond.image ? (
          <Image
            source={{ uri: normalizeReferralMediaUri(cond.image) }}
            style={tierStyles.rewardImage}
            resizeMode="cover"
          />
        ) : null}
        <View style={tierStyles.rewardInfo}>
          <Text style={[tierStyles.rewardName, status === 'claimed' && tierStyles.textMuted]}>
            {cond.reward || '—'}
          </Text>
          {statusLabel && (
            <Text style={[tierStyles.statusLabel, { color: statusColor }]}>
              {statusLabel}
            </Text>
          )}
          {status === 'locked' && (
            insufficient ? (
              <Text style={tierStyles.insufficientHint} numberOfLines={3}>
                Poin tidak cukup untuk klaim (sisa {remSafe}, per klaim butuh {termsNeed}). Tambah transaksi referral
                yang sukses untuk menambah poin.
              </Text>
            ) : (
              <Text style={tierStyles.lockedHint} numberOfLines={2}>
                Kumpulkan minimal {termsNeed} poin (satu transaksi referral sukses biasanya = 1 poin).
              </Text>
            )
          )}
        </View>
      </View>

      {/* tier action */}
      {status === 'claimable' && !addressReady && (
        <TouchableOpacity
          style={[tierStyles.claimBtn, { backgroundColor: '#64748B' }]}
          onPress={onNeedAddress}
          activeOpacity={0.8}
        >
          <Ionicons name="location-outline" size={15} color="#fff" style={{ marginRight: 4 }} />
          <Text style={tierStyles.claimBtnText}>Atur alamat pengiriman</Text>
        </TouchableOpacity>
      )}
      {status === 'claimable' && addressReady && (
        <TouchableOpacity
          style={[tierStyles.claimBtn, claiming && tierStyles.claimBtnDisabled]}
          onPress={onClaim}
          disabled={claiming}
          activeOpacity={0.8}
        >
          {claiming ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <>
              <Ionicons name="gift" size={15} color="#fff" style={{ marginRight: 4 }} />
              <Text style={tierStyles.claimBtnText}>Klaim Hadiah</Text>
            </>
          )}
        </TouchableOpacity>
      )}
      {status === 'claimed' && (() => {
        const orderStatus = cond.claim_order_status;
        const statusCode  = orderStatus?.code ?? 2;
        const statusLabelOrder = orderStatus?.label ?? 'Pesanan Di Proses';
        const statusColorOrder = orderStatus?.color ?? '#3B82F6';
        const isCompleted = statusCode === 5;
        const isCancelled = statusCode === 6 || statusCode === 9 || statusCode === 10;
        const iconName    = isCompleted
          ? 'checkmark-done-circle-outline'
          : isCancelled
          ? 'close-circle-outline'
          : 'time-outline';
        const needMore = Math.max(0, termsNeed - remSafe);
        return (
          <View style={tierStyles.claimedBadge}>
            <View style={tierStyles.claimedBadgeRow}>
              <Ionicons name="checkmark-circle" size={14} color="#10B981" />
              <Text style={tierStyles.claimedBadgeText}>Sudah pernah diklaim</Text>
            </View>
            <View style={[tierStyles.processingBadgeRow, { backgroundColor: statusColorOrder + '18' }]}>
              <Ionicons name={iconName} size={12} color={statusColorOrder} />
              <Text style={[tierStyles.processingBadgeText, { color: statusColorOrder }]}>{statusLabelOrder}</Text>
            </View>
            {needMore > 0 ? (
              <Text style={tierStyles.claimAgainHint} numberOfLines={3}>
                Klaim lagi hadiah ini: butuh {needMore} poin lagi (sisa poin Anda sekarang: {remSafe}). Setiap klaim
                memakai {termsNeed} poin dari total transaksi referral Anda.
              </Text>
            ) : null}
          </View>
        );
      })()}
    </View>
  );
}

// ── Progress bar component ───────────────────────────────────────────────────
function ProgressBar({ value, max, color }) {
  const pct = max > 0 ? Math.min(1, value / max) : 0;
  return (
    <View style={progStyles.track}>
      <View style={[progStyles.fill, { width: `${Math.round(pct * 100)}%`, backgroundColor: color }]} />
    </View>
  );
}

// ── Main screen ──────────────────────────────────────────────────────────────
export default function ReferralScreen() {
  const navigation = useNavigation();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [availablePromo, setAvailablePromo] = useState(null);
  const [claimingKey, setClaimingKey] = useState(null); // key: `${linkId}_${conditionIndex}` for per-tier loading state
  const [notificationBanner, setNotificationBanner] = useState(null);
  const [joinCodeModal, setJoinCodeModal] = useState(false);
  const [joinTargetPromo, setJoinTargetPromo] = useState(null);

  const joinablePromotions = useMemo(
    () => normalizeReferralPromotions(availablePromo),
    [availablePromo],
  );

  const goShippingAddressForm = useCallback(() => {
    navigation.navigate('ShippingAddressesList', { returnToReferral: true });
  }, [navigation]);

  const loadData = useCallback(async () => {
    try {
      const [linksRes, availableRes] = await Promise.all([
        getReferralLinks(),
        getReferralAvailable(),
      ]);
      const newLinks = Array.isArray(linksRes) ? linksRes : [];

      const hasRewardReady    = newLinks.some((l) => l.can_claim === true);
      const hasRewardProcessing = newLinks.some(
        (l) => (l.claimed_indices ?? []).length > 0 && !l.can_claim
      );

      let newTransactionBanner = false;
      try {
        const lastState = await AsyncStorage.getItem(REFERRAL_LAST_STATE_KEY);
        const lastCounts = lastState ? JSON.parse(lastState) : {};
        if (lastState) {
          newLinks.forEach((l) => {
            const prev = lastCounts[l.id] ?? 0;
            if ((l.purchase_count ?? 0) > prev) newTransactionBanner = true;
          });
        }
        const newCounts = {};
        newLinks.forEach((l) => { newCounts[l.id] = l.purchase_count ?? 0; });
        await AsyncStorage.setItem(REFERRAL_LAST_STATE_KEY, JSON.stringify(newCounts));
      } catch (_) {}

      if (hasRewardReady) {
        const addrOk = newLinks.length === 0 || newLinks[0].has_shipping_address !== false;
        if (!addrOk) {
          setNotificationBanner({
            type: 'need_address',
            message:
              'Syarat hadiah terpenuhi. Tambah alamat lewat tombol «Atur alamat» atau Profil → Alamat pengiriman, lalu refresh halaman ini.',
            icon: 'location',
            color: '#F97316',
          });
        } else {
          setNotificationBanner({
            type: 'reward_ready',
            message: 'Ada hadiah yang siap diklaim! Lihat kondisi yang berwarna kuning.',
            icon: 'gift',
            color: '#F59E0B',
          });
        }
      } else if (newTransactionBanner) {
        setNotificationBanner({
          type: 'new_transaction',
          message: 'Ada transaksi baru dari referral link Anda!',
          icon: 'cart',
          color: '#3B82F6',
        });
      } else if (hasRewardProcessing) {
        setNotificationBanner({
          type: 'reward_processing',
          message: 'Hadiah Anda sedang diproses dan akan segera dikirim.',
          icon: 'time',
          color: '#10B981',
        });
      } else {
        setNotificationBanner(null);
      }

      setLinks(newLinks);
      setAvailablePromo(availableRes?.available ? availableRes : null);
      return newLinks;
    } catch (e) {
      console.warn('Referral load error:', e.message);
      setLinks([]);
      setAvailablePromo(null);
      setNotificationBanner(null);
      return null;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { loadData(); }, [loadData]));
  const onRefresh = () => { setRefreshing(true); loadData(); };

  const handleJoinPress = (promo) => {
    const p = promo || joinablePromotions[0];
    if (!p?.id_promosi) return;
    setJoinTargetPromo(p);
    setJoinCodeModal(true);
  };

  const handleJoin = async () => {
    if (!joinTargetPromo?.id_promosi) return;
    setJoinLoading(true);
    try {
      await joinPromotion(joinTargetPromo.id_promosi);
      setJoinCodeModal(false);
      setJoinTargetPromo(null);
      await loadData();
      Alert.alert('Berhasil', 'Anda sekarang POL. Kode referral otomatis Anda tersedia di bawah.');
    } catch (e) {
      Alert.alert('Gagal', e.message || 'Gagal gabung POL');
    } finally {
      setJoinLoading(false);
    }
  };

  const handleShare = async (item) => {
    try {
      const refUrl = item.referral_url || (item.code ? `https://zoolyfe.id/r/${item.code}` : '');
      const message =
        item.share_message ||
        [item.promo_name ? `Kode referral — ${item.promo_name}` : null, item.scope_line || null, refUrl]
          .filter(Boolean)
          .join('\n');
      const title = item.promo_name ? `Referral — ${item.promo_name}` : 'Kode Referral Zoolyfe';
      await Share.share({ message, title });
    } catch (e) {
      if (e.message !== 'User did not share') Alert.alert('Error', e.message);
    }
  };

  const handleClaim = async (linkId, conditionIndex, linkItem) => {
    if (linkItem && linkItem.has_shipping_address === false) {
      goShippingAddressForm();
      return;
    }
    const key = `${linkId}_${conditionIndex}`;
    setClaimingKey(key);
    try {
      const claimOpts =
        linkItem?.default_alamat_id > 0 ? { idAlamat: linkItem.default_alamat_id } : {};
      await claimReward(linkId, conditionIndex, claimOpts);

      // Full reload from server (points, tiers, claim history); avoids stale UI without pull-to-refresh.
      try {
        await loadData();
      } catch (syncErr) {
        console.warn('Referral post-claim reload:', syncErr?.message);
        await new Promise((r) => setTimeout(r, 450));
        try {
          await loadData();
        } catch (e2) {
          console.warn('Referral post-claim retry:', e2?.message);
        }
      }

      Alert.alert('Berhasil!', 'Hadiah Anda akan diproses dan dikirimkan segera.');
    } catch (e) {
      const code = e?.apiErrorCode;
      if (code === 'ADDRESS_REQUIRED' || code === 'ADDRESS_INCOMPLETE') {
        Alert.alert(
          'Alamat pengiriman',
          (e.message || 'Lengkapi alamat terlebih dahulu.') +
            '\n\nAnda bisa mengisi di app (Alamat pengiriman) atau lewat zoolyfe.id.',
          [
            { text: 'Nanti', style: 'cancel' },
            {
              text: 'Menu Profil',
              onPress: () => goToProfileTab(navigation),
            },
            {
              text: 'Buka Zoolyfe.id',
              onPress: () => {
                openZoolyfeIdSso().catch((err) =>
                  Alert.alert('Gagal', err?.message || 'Coba dari Menu Profil.'),
                );
              },
            },
            { text: 'Isi di app', onPress: goShippingAddressForm },
          ],
        );
      } else {
        Alert.alert('Gagal', e.message || 'Gagal klaim hadiah');
      }
    } finally {
      setClaimingKey(null);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <AppHeader title="Kode Referral Saya" onBack={() => navigation.goBack()} />
        <View style={styles.centered}>
          <ActivityIndicator size="large" color="#64748B" />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <AppHeader title="Kode Referral Saya" onBack={() => navigation.goBack()} />

      {/* Notification banner */}
      {notificationBanner && (
        <View style={[
          styles.notificationBanner,
          { backgroundColor: notificationBanner.color + '18', borderLeftColor: notificationBanner.color },
        ]}>
          <Pressable
            style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}
            onPress={() => {
              if (notificationBanner.type === 'need_address') {
                goShippingAddressForm();
              }
            }}
          >
            <Ionicons
              name={
                notificationBanner.icon === 'gift' ? 'gift-outline'
                : notificationBanner.icon === 'cart' ? 'cart-outline'
                : notificationBanner.icon === 'time' ? 'time-outline'
                : notificationBanner.icon === 'location' ? 'location-outline'
                : 'checkmark-circle-outline'
              }
              size={20} color={notificationBanner.color} style={{ marginRight: 10 }}
            />
            <Text style={[styles.notificationText, { color: notificationBanner.color }]}>
              {notificationBanner.message}
              {notificationBanner.type === 'need_address' ? '\n\nKetuk di sini untuk isi alamat.' : ''}
            </Text>
          </Pressable>
          <TouchableOpacity onPress={() => setNotificationBanner(null)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Ionicons name="close" size={18} color={notificationBanner.color} />
          </TouchableOpacity>
        </View>
      )}

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {/* Empty state */}
        {links.length === 0 && (
          <View style={styles.emptyBlock}>
            <Ionicons name="ribbon-outline" size={48} color="#CBD5E1" style={{ marginBottom: 12 }} />
            <Text style={styles.emptyTitle}>Belum ada kode referral</Text>
            <Text style={styles.emptySub}>
              Gabung sebagai POL (Partner of Zoolyfe) untuk mendapatkan kode referral dan berbagai hadiah menarik.
            </Text>
            {joinablePromotions.length > 0 ? (
              joinablePromotions.length === 1 ? (
                <>
                  {joinablePromotions[0].scope_line ? (
                    <Text style={styles.availableScopeHint} numberOfLines={3}>
                      {joinablePromotions[0].scope_line}
                    </Text>
                  ) : null}
                  <TouchableOpacity style={styles.joinBtn} onPress={() => handleJoinPress(joinablePromotions[0])}>
                    <Ionicons name="person-add-outline" size={18} color="#fff" style={{ marginRight: 6 }} />
                    <Text style={styles.joinBtnText}>Gabung Sekarang</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <>
                  <Text style={[styles.emptySub, { marginBottom: 12 }]}>
                    Beberapa program POL sedang aktif. Pilih salah satu:
                  </Text>
                  {joinablePromotions.map((p) => (
                    <TouchableOpacity
                      key={p.id_promosi}
                      style={styles.programChoiceBtn}
                      onPress={() => handleJoinPress(p)}
                      activeOpacity={0.85}
                    >
                      <View style={{ flex: 1 }}>
                        <Text style={styles.programChoiceTitle}>{p.nama_promosi}</Text>
                        {p.scope_line ? (
                          <Text style={styles.programChoiceScope} numberOfLines={3}>
                            {p.scope_line}
                          </Text>
                        ) : null}
                      </View>
                      <Ionicons name="chevron-forward" size={20} color="#94A3B8" />
                    </TouchableOpacity>
                  ))}
                </>
              )
            ) : (
              <Text style={styles.noPromoText}>Tidak ada program referral yang tersedia saat ini.</Text>
            )}
          </View>
        )}

        {/* referral card per link */}
        {joinablePromotions.length > 0 && links.length > 0 && (
          <View style={styles.moreProgramsSection}>
            <Text style={styles.moreProgramsTitle}>Gabung program POL lain</Text>
            <Text style={styles.moreProgramsSub}>
              Anda sudah punya kode di atas; berikut program lain yang masih bisa diikuti (periode & kuota tersedia).
            </Text>
            {joinablePromotions.map((p) => (
              <TouchableOpacity
                key={p.id_promosi}
                style={styles.programChoiceBtn}
                onPress={() => handleJoinPress(p)}
                activeOpacity={0.85}
              >
                <View style={{ flex: 1 }}>
                  <Text style={styles.programChoiceTitle}>{p.nama_promosi}</Text>
                  {p.scope_line ? (
                    <Text style={styles.programChoiceScope} numberOfLines={2}>
                      {p.scope_line}
                    </Text>
                  ) : null}
                </View>
                <Ionicons name="add-circle-outline" size={22} color="#FF6B35" />
              </TouchableOpacity>
            ))}
          </View>
        )}

        {links.map((item) => {
          const purchaseCount         = item.purchase_count ?? 0;
          const claimedIndices        = item.claimed_indices ?? [];
          const remainingTransactions = referralRemainingPoints(item);
          const conditions            = item.conditions ?? [];
          const totalTarget           = conditions.length > 0
            ? conditions[conditions.length - 1].terms_count
            : (item.target_count ?? 1);

          // Remaining pool can unlock a tier repeatedly; claimable beats "claimed" UI state.
          const condStatuses = conditions.map((cond) => {
            if (remainingTransactions >= (cond.terms_count ?? 1)) return 'claimable';
            if (cond.claim_order_status) return 'claimed';
            return 'locked';
          });

          const condInsufficient = conditions.map((cond) => {
            const terms = cond.terms_count ?? 1;
            return purchaseCount >= terms && remainingTransactions < terms;
          });

          const allDone =
            conditions.length > 0 &&
            !item.can_claim &&
            remainingTransactions === 0 &&
            purchaseCount > 0;
          const hasClaimed = claimedIndices.length > 0;

          return (
            <View key={item.id} style={styles.card}>
              {/* promo name */}
              <Text style={styles.promoName}>{item.promo_name || 'Referral'}</Text>
              {item.scope_line ? (
                <Text style={styles.scopeLine} numberOfLines={4}>
                  {item.scope_line}
                </Text>
              ) : null}

              {/* referral code */}
              <View style={styles.codeRow}>
                <Text style={styles.codeLabel}>Kode</Text>
                <Text style={styles.codeValue}>{item.code}</Text>
              </View>

              {/* transaction summary + progress bar */}
              <View style={styles.progressSummary}>
                <View style={styles.progressTopRow}>
                  <View style={{ flex: 1, marginRight: 8 }}>
                    <Text style={styles.progressCount}>
                      <Text style={{ fontWeight: '700', color: '#0F172A' }}>{purchaseCount}</Text>
                      <Text style={{ color: '#64748B' }}> transaksi referral (total)</Text>
                    </Text>
                    <Text style={styles.progressSubCount}>
                      Sisa poin untuk klaim:{' '}
                      <Text style={{ fontWeight: '700', color: '#0F172A' }}>{remainingTransactions}</Text>
                      {' · '}
                      syarat tertinggi satu klaim: {totalTarget} poin
                    </Text>
                  </View>
                  {allDone && (
                    <View style={styles.allDoneBadge}>
                      <Ionicons name="trophy" size={12} color="#F59E0B" />
                      <Text style={styles.allDoneText}>Semua diklaim!</Text>
                    </View>
                  )}
                </View>
                <ProgressBar
                  value={purchaseCount}
                  max={totalTarget}
                  color={item.can_claim ? '#F59E0B' : '#3B82F6'}
                />
              </View>

              {/* section header + remaining points badge */}
              <View style={styles.tierDivider}>
                <View style={styles.tierDividerRow}>
                  <Text style={styles.tierSectionTitle}>Progress Hadiah</Text>
                  {hasClaimed && remainingTransactions < purchaseCount && (
                    <View style={styles.remainingPoinBadge}>
                      <Ionicons name="flash-outline" size={12} color="#F97316" />
                      <Text style={styles.remainingPoinText}>
                        Sisa poin: {remainingTransactions}
                      </Text>
                    </View>
                  )}
                </View>
              </View>

              {/* tier list — all qualifying conditions show a claim button */}
              {conditions.map((cond, index) => {
                const status = condStatuses[index];
                const key    = `${item.id}_${index}`;
                return (
                  <TierRow
                    key={index}
                    cond={cond}
                    index={index}
                    status={status}
                    onClaim={() => handleClaim(item.id, index, item)}
                    claiming={claimingKey === key}
                    insufficient={condInsufficient[index]}
                    remainingForInfo={remainingTransactions}
                    addressReady={item.has_shipping_address !== false}
                    onNeedAddress={goShippingAddressForm}
                  />
                );
              })}

              {Array.isArray(item.claim_history) && item.claim_history.length > 0 ? (
                <View style={styles.historySection}>
                  <Text style={styles.historyTitle}>Riwayat klaim hadiah</Text>
                  <Text style={styles.historySub}>
                    Anda bisa klaim tingkat yang sama lebih dari satu kali selama poin mencukupi. Berikut ringkasan klaim
                    sebelumnya (terbaru di atas).
                  </Text>
                  {item.claim_history.map((h) => {
                    const title = (h.reward && String(h.reward).trim()) || h.reward_description || 'Hadiah';
                    const st = h.order_status || {};
                    return (
                      <View key={`h-${h.id}`} style={styles.historyRow}>
                        <View style={{ flex: 1, marginRight: 8 }}>
                          <Text style={styles.historyRewardTitle} numberOfLines={2}>
                            {title}
                          </Text>
                          <Text style={styles.historyMeta} numberOfLines={2}>
                            Kondisi {(h.condition_index ?? 0) + 1}
                            {h.terms_count != null ? ` · butuh ${h.terms_count} poin/klaim` : ''}
                            {h.no_transaksi ? ` · ${h.no_transaksi}` : ''}
                            {h.claimed_at ? `\n${formatClaimedAtTimestamp(h.claimed_at)}` : ''}
                          </Text>
                        </View>
                        <View
                          style={[
                            styles.historyStatusPill,
                            { backgroundColor: `${st.color || '#64748B'}22` },
                          ]}
                        >
                          <Text
                            style={[styles.historyStatusText, { color: st.color || '#64748B' }]}
                            numberOfLines={2}
                          >
                            {st.label || '—'}
                          </Text>
                        </View>
                      </View>
                    );
                  })}
                </View>
              ) : null}

              {/* card footer */}
              <View style={styles.cardFooter}>
                <TouchableOpacity style={styles.shareBtn} onPress={() => handleShare(item)}>
                  <Ionicons name="share-outline" size={18} color="#64748B" />
                  <Text style={styles.shareBtnText}>Bagikan Link</Text>
                </TouchableOpacity>
                {item.can_claim && (
                  <View style={styles.claimHintBadge}>
                    <Ionicons name="gift-outline" size={14} color="#F59E0B" />
                    <Text style={styles.claimHintText}>Ada hadiah siap diklaim ↑</Text>
                  </View>
                )}
              </View>
            </View>
          );
        })}
      </ScrollView>

      {/* join POL modal */}
      <Modal
        visible={joinCodeModal}
        transparent
        animationType="fade"
        onRequestClose={() => {
          if (!joinLoading) {
            setJoinCodeModal(false);
            setJoinTargetPromo(null);
          }
        }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.codeModalOverlay}
        >
          <View style={styles.codeModalBox}>
            <Text style={styles.codeModalTitle}>Buat Kode Referral Kamu</Text>
            {joinTargetPromo?.nama_promosi ? (
              <Text style={{ fontSize: 15, fontWeight: '700', color: '#0F172A', textAlign: 'center', marginBottom: 6 }}>
                {joinTargetPromo.nama_promosi}
              </Text>
            ) : null}
            {joinTargetPromo?.scope_line ? (
              <Text style={styles.codeModalScope} numberOfLines={4}>
                {joinTargetPromo.scope_line}
              </Text>
            ) : null}
            <Text style={styles.codeModalSub}>
              Kode referral akan dibuat otomatis dari brand program (atau nama program), nama depan profil Anda, dan 3–4
              digit terakhir nomor HP di profil. Pastikan nama dan nomor HP sudah benar di menu Profil.
            </Text>
            <View style={styles.codeModalActions}>
              <TouchableOpacity
                style={styles.codeBtnCancel}
                onPress={() => {
                  setJoinCodeModal(false);
                  setJoinTargetPromo(null);
                }}
                disabled={joinLoading}
              >
                <Text style={styles.codeBtnCancelText}>Nanti</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.codeBtnJoin, joinLoading && styles.codeBtnDisabled]}
                onPress={handleJoin}
                disabled={joinLoading}
              >
                {joinLoading ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.codeBtnJoinText}>Gabung Sekarang</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}

// ── TierRow styles ────────────────────────────────────────────────────────────
const tierStyles = StyleSheet.create({
  row: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#F8FAFC',
    padding: 12,
    marginBottom: 8,
  },
  rowClaimable: {
    borderColor: '#FCD34D',
    backgroundColor: '#FFFBEB',
  },
  rowClaimed: {
    borderColor: '#D1FAE5',
    backgroundColor: '#F0FDF4',
  },
  tierHead: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 6,
  },
  badge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1.5,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tierLabel: {
    fontSize: 13,
    fontWeight: '700',
  },
  termsText: {
    fontSize: 12,
    color: '#64748B',
    flex: 1,
    textAlign: 'right',
  },
  body: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  rewardImage: {
    width: 56,
    height: 56,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#F1F5F9',
  },
  rewardInfo: { flex: 1 },
  rewardName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 3,
  },
  textMuted: { color: '#94A3B8' },
  statusLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  lockedHint: {
    fontSize: 12,
    color: '#94A3B8',
  },
  insufficientHint: {
    fontSize: 12,
    color: '#F97316',
    fontWeight: '500',
  },
  claimBtn: {
    flexDirection: 'row',
    backgroundColor: '#F59E0B',
    borderRadius: 8,
    paddingVertical: 9,
    paddingHorizontal: 14,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
  },
  claimBtnDisabled: { opacity: 0.65 },
  claimBtnText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 14,
  },
  claimedBadge: {
    alignSelf: 'flex-start',
    borderRadius: 6,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#D1FAE5',
  },
  claimedBadgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: '#D1FAE5',
  },
  claimedBadgeText: {
    fontSize: 12,
    color: '#10B981',
    fontWeight: '600',
  },
  processingBadgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 3,
    paddingHorizontal: 8,
    backgroundColor: '#FEF3C7',
  },
  processingBadgeText: {
    fontSize: 11,
    color: '#D97706',
    fontWeight: '500',
  },
  claimAgainHint: {
    fontSize: 11,
    color: '#475569',
    fontWeight: '500',
    paddingHorizontal: 8,
    paddingBottom: 6,
    paddingTop: 4,
    lineHeight: 16,
    backgroundColor: '#F8FAFC',
  },
});

// ── ProgressBar styles ────────────────────────────────────────────────────────
const progStyles = StyleSheet.create({
  track: {
    height: 6,
    borderRadius: 3,
    backgroundColor: '#E2E8F0',
    overflow: 'hidden',
    marginTop: 6,
  },
  fill: {
    height: '100%',
    borderRadius: 3,
  },
});

// ── Screen styles ─────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  scroll: { flex: 1 },
  scrollContent: { padding: 16, paddingBottom: 32 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },

  // Empty state
  emptyBlock: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 28,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  emptyTitle: { fontSize: 17, fontWeight: '700', color: '#1E293B', marginBottom: 8 },
  emptySub: { fontSize: 14, color: '#64748B', textAlign: 'center', marginBottom: 20, lineHeight: 20 },
  joinBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1E293B',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
  },
  joinBtnText: { color: '#fff', fontWeight: '700', fontSize: 15 },
  noPromoText: { fontSize: 14, color: '#94A3B8' },
  programChoiceBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    padding: 14,
    marginBottom: 10,
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  programChoiceTitle: { fontSize: 15, fontWeight: '700', color: '#0F172A' },
  programChoiceScope: { fontSize: 12, color: '#64748B', marginTop: 4, lineHeight: 18 },
  moreProgramsSection: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  moreProgramsTitle: { fontSize: 16, fontWeight: '700', color: '#1E293B', marginBottom: 6 },
  moreProgramsSub: { fontSize: 13, color: '#64748B', marginBottom: 12, lineHeight: 18 },

  // Notification banner
  notificationBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 4,
    borderRadius: 8,
    borderLeftWidth: 4,
  },
  notificationText: { flex: 1, fontSize: 13, fontWeight: '500' },

  // Card
  card: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  promoName: { fontSize: 16, fontWeight: '700', color: '#1E293B', marginBottom: 8 },
  scopeLine: { fontSize: 13, color: '#64748B', marginBottom: 10, lineHeight: 18 },
  availableScopeHint: {
    fontSize: 13,
    color: '#475569',
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 18,
    paddingHorizontal: 8,
  },
  codeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    alignSelf: 'flex-start',
  },
  codeLabel: { fontSize: 12, color: '#64748B' },
  codeValue: { fontSize: 16, fontWeight: '700', color: '#0F172A', letterSpacing: 1.5 },

  // Progress summary
  progressSummary: { marginBottom: 12 },
  progressTopRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 2 },
  progressCount: { fontSize: 14 },
  progressSubCount: { fontSize: 12, color: '#64748B', marginTop: 4, lineHeight: 17 },
  allDoneBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: '#FEF3C7', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3,
  },
  allDoneText: { fontSize: 12, color: '#D97706', fontWeight: '600' },

  // Tier section header
  tierDivider: {
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
    paddingTop: 12,
    marginBottom: 8,
  },
  tierDividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tierSectionTitle: { fontSize: 13, fontWeight: '600', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 },

  historySection: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
  },
  historyTitle: { fontSize: 13, fontWeight: '700', color: '#475569', marginBottom: 4 },
  historySub: { fontSize: 12, color: '#94A3B8', lineHeight: 17, marginBottom: 10 },
  historyRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 10,
    paddingHorizontal: 10,
    marginBottom: 8,
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  historyRewardTitle: { fontSize: 13, fontWeight: '600', color: '#0F172A', marginBottom: 4 },
  historyMeta: { fontSize: 11, color: '#64748B', lineHeight: 16 },
  historyStatusPill: {
    maxWidth: '38%',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
    justifyContent: 'center',
  },
  historyStatusText: { fontSize: 11, fontWeight: '600', textAlign: 'right' },

  remainingPoinBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: '#FFF7ED',
    borderRadius: 6,
    paddingVertical: 3,
    paddingHorizontal: 7,
    borderWidth: 1,
    borderColor: '#FDBA74',
  },
  remainingPoinText: {
    fontSize: 11,
    color: '#F97316',
    fontWeight: '600',
  },

  // Card footer
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
  },
  shareBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
    gap: 6,
  },
  shareBtnText: { fontSize: 14, color: '#64748B', fontWeight: '500' },
  claimHintBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: '#FEF3C7', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4,
  },
  claimHintText: { fontSize: 12, color: '#D97706', fontWeight: '600' },

  // join POL modal
  codeModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  codeModalBox: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
  },
  codeModalTitle: { fontSize: 18, fontWeight: '700', color: '#0F172A', marginBottom: 6 },
  codeModalScope: { fontSize: 13, color: '#475569', marginBottom: 10, lineHeight: 18 },
  codeModalSub: { fontSize: 14, color: '#64748B', marginBottom: 16, lineHeight: 20 },
  codeInput: {
    borderWidth: 1.5,
    borderColor: '#CBD5E1',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 18,
    fontWeight: '700',
    color: '#0F172A',
    letterSpacing: 2,
    textAlign: 'center',
    backgroundColor: '#F8FAFC',
    marginBottom: 8,
  },
  codeInputError: { borderColor: '#EF4444', backgroundColor: '#FEF2F2' },
  codeInputOk: { borderColor: '#10B981', backgroundColor: '#F0FDF4' },
  codeHintRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 16 },
  codeHintText: { fontSize: 13, color: '#64748B' },
  codeModalActions: { flexDirection: 'row', gap: 10, marginTop: 8 },
  codeBtnCancel: {
    flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#F1F5F9', alignItems: 'center',
  },
  codeBtnCancelText: { fontSize: 15, fontWeight: '600', color: '#64748B' },
  codeBtnJoin: {
    flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#1E293B', alignItems: 'center',
  },
  codeBtnDisabled: { opacity: 0.35 },
  codeBtnJoinText: { fontSize: 15, fontWeight: '600', color: '#fff' },
});
